import { useState } from "react";

import {
  faChartMixed,
  faFlask,
  faWavePulse,
} from "@fortawesome/pro-regular-svg-icons";
import { AnimatePresence, m } from "framer-motion";
import { noop } from "lodash-es";

import { useFlow } from "src/api/queries";
import { CopilotButton } from "src/base-components/CopilotButton";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { EmptyState } from "src/design-system/EmptyState";
import { CopilotSidebar } from "src/flow/analytics/CopilotSidebar";
import { FlowPageLayout } from "src/flow/FlowPageLayout";
import { SubHeader } from "src/flow/SubHeader";
import { useEnvironment } from "src/hooks/useEnvironment";
import { FlowPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const FlowAnalyticsContent: React.FC = () => {
  const { flow_id } = useParamsDecode<FlowPageParams>();
  const { data: flow } = useFlow(flow_id, {
    refetchOnMount: true,
  });
  const [environment, setEnvironment] = useEnvironment();
  const [isCopilotOpen, setIsCopilotOpen] = useState(false);

  return (
    <FlowPageLayout
      flow={flow}
      headerActions={
        <AnimatePresence>
          {!isCopilotOpen && (
            <m.div
              key="ask-copilot"
              className="pr-3"
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              initial={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.15 }}
            >
              <CopilotButton
                dataLoc="ask-copilot-button"
                onClick={() => setIsCopilotOpen(true)}
              >
                Ask Copilot
              </CopilotButton>
            </m.div>
          )}
        </AnimatePresence>
      }
      rightPanel={
        <AnimatePresence>
          {isCopilotOpen && flow && (
            <CopilotSidebar
              flowId={flow_id}
              flowName={flow.name}
              onClose={() => setIsCopilotOpen(false)}
            />
          )}
        </AnimatePresence>
      }
    >
      <SubHeader title="Analytics" paddedParent>
        <SubHeader.DatePicker value={undefined} onChange={noop} />
        <span className="h-full w-12 border-gray-200 border-l p-3.5">
          <EllipsisOptionsDropdown
            elements={[
              {
                key: `Switch to ${environment === "sandbox" ? "Live" : "Sandbox"}`,
                icon: environment === "sandbox" ? faWavePulse : faFlask,
                action: () =>
                  setEnvironment(
                    environment === "sandbox" ? "live" : "sandbox",
                  ),
              },
            ]}
            iconSize="sm"
            placement="bottom start"
          />
        </span>
      </SubHeader>
      <EmptyState
        description="Analytics for this Decision Flow will appear here."
        headline="Analytics"
        icon={faChartMixed}
      />
    </FlowPageLayout>
  );
};
